import React, { useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PolarArea } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";


const imageUrl = process.env.REACT_APP_IMAGE_URL;

// PDF Generation Constants
const PDF_CONFIG = {
  orientation: "landscape",
  format: "a4",
  targetWidth: 1400,
  targetHeight: 990,
  html2canvasScale: 2.5,
  jpegQuality: 0.95,
};

const CHART_RENDER_DELAY_MS = 1000;
const PDF_START_DELAY_MS = 100;

const DEFAULT_RADAR_DATASET = {
  data: [],
  backgroundColor: [],
  borderWidth: 0.2,
  borderColor: [],
  circular: "inner",
  borderJoinStyle: "round",
};

// Larger typography for PDF readability
const PDF_FONT = {
  xs: "14px",
  sm: "16px",
  base: "18px",
  lg: "20px",
  xl: "24px",
  "2xl": "28px",
  "3xl": "32px",
};

// Dashboard Page Component
const DashboardPage = ({
  t,
  user,
  radarData,
  strength,
  weakness,
}) => {
  const chartOptions = React.useMemo(() => ({
    animation: { duration: 0 },
    plugins: {
      ChartDataLabels,
      datalabels: {
        formatter: function (value, context) {
            const label = radarData?.short_labels?.[context.dataIndex];
            return (label ?? "").toString().toUpperCase();
        },
        anchor: "end",
        align: "center",
        font: {
          family: "Poppins, sans-serif",
          weight: "bold",
          size: 14,
        },
      },
      legend: {
        display: false,
      },
    },
    clip: { left: 0, top: 0, right: 0, bottom: 0 },
    scales: {
      r: {
        min: 0,
        max: 6,
        angleLines: {
          display: true,
        },
        ticks: {
          maxTicksLimit: 1,
          display: false,
        },
        pointLabels: {
          display: false,
        },
      },
    },
  }), [radarData]);

  // Safe chart data so PolarArea never receives undefined labels/datasets
  const safeRadarData = React.useMemo(
    () => ({
      labels: radarData?.labels ?? [],
      short_labels: radarData?.short_labels ?? [],
      datasets: radarData?.datasets ?? [DEFAULT_RADAR_DATASET],
    }),
    [radarData]
  );

  return (
    <div className="w-full bg-white p-6" style={{ width: "100%", backgroundColor: "#ffffff" }}>
        {/* Header */}
        <div className="mb-8">
            <h2 className="title-primary mb-2" style={{ fontSize: PDF_FONT["3xl"] }}>{t("surveyResults.page_title")}</h2>
            <p className="subtitle mb-5" style={{ fontSize: PDF_FONT.base }}>
                {user.first_name} {user.last_name} ({user.email})
            </p>
        </div>

      <div className="bg-[#E9E9E9] flex flex-wrap rounded-[15px] p-6 mb-8">
        <div className="w-1/2 text-center bg-white rounded-[15px] pt-3 pb-3 px-1">
          <h3 className="title-primary-sm mb-1 uppercase" style={{ fontSize: PDF_FONT.xl }}>
            {t("surveyResults.summary_results_scorecard")}
          </h3>
          <PolarArea
            className="mx-auto max-w-full p-2 !w-[350px] !h-[350px]"
            data={safeRadarData}
            options={chartOptions}
          />
          
          {/* Legend below chart with full titles - aligned to right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px', paddingRight: '16px' }}>
            <div style={{ paddingLeft: '8px' }}>
              {safeRadarData.labels.map((label, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '0px',
                  margin: '0px',
                  fontSize: PDF_FONT.sm,
                  lineHeight: '1.2'
                }}>
                  <span
                    className="legend-box"
                    style={{ 
                      color: safeRadarData.datasets[0]?.backgroundColor?.[index] ?? '#999',
                      fontSize: '28px', 
                      lineHeight: '1',
                      verticalAlign: 'middle',
                      flexShrink: 0,
                      display: 'inline-block',
                      position: 'relative'
                    }}
                  >
                    ■
                  </span>
                  <span style={{ 
                    fontSize: PDF_FONT.sm, 
                    fontWeight: '600',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                    lineHeight: '1.2'
                  }}>
                    {label}
                  </span>
                  <span style={{ 
                    fontSize: PDF_FONT.sm, 
                    fontWeight: '500',
                    color: '#666',
                    marginLeft: '8px',
                    verticalAlign: 'middle',
                    lineHeight: '1.2'
                  }}>
                    {safeRadarData.datasets[0]?.data?.[index]?.toFixed(1) ?? '0.0'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-1/2 pt-3 pl-8">
          <div className="mb-7">
            <h3 className="title-primary-sm mb-3" style={{ fontSize: PDF_FONT.xl }}>
              {t("surveyResults.strength.title")}
            </h3>
            <div>
              <div className="align-top inline-block">
                <span className={`icon-strength-weakness bg-${strength.title} mr-2`}>
                  <img
                    className="icon-inner-img"
                    src={imageUrl + strength.img}
                    alt={strength.title}
                  />
                </span>
              </div>
              <div className="align-middle inline-block w-2/3">
                <h4 className="title-base" style={{ fontSize: PDF_FONT.lg }}>{strength.title}</h4>
                <p className="subtitle" style={{ fontSize: PDF_FONT.base }}>{strength.text}</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="title-primary-sm mb-3" style={{ fontSize: PDF_FONT.xl }}>
              {t("surveyResults.weakness.title")}
            </h3>
            <div>
              <div className="align-top inline-block">
                <span className={`icon-strength-weakness bg-${weakness.title} mr-2`}>
                  <img
                    className="icon-inner-img"
                    src={imageUrl + weakness.img}
                    alt={weakness.title}
                  />
                </span>
              </div>
              <div className="align-middle inline-block w-2/3">
                <h4 className="title-base" style={{ fontSize: PDF_FONT.lg }}>{weakness.title}</h4>
                <p className="subtitle" style={{ fontSize: PDF_FONT.base }}>{weakness.text}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Category Page - PDF-optimized compact layout
const PDFCategoryPage = ({ category, t }) => {
    // Calculate rating
    let sum_rating = category?.sum_rating;
    let sum_responses = category?.sum_responses;
    let avg_rating = (sum_rating / sum_responses) * 6;
    let rating = avg_rating % 1 === 0 ? avg_rating : (avg_rating || 0).toFixed(1);

    return (
      <div className="w-full bg-white p-6" style={{ width: "100%", backgroundColor: "#ffffff", padding: "32px" }}>
        {/* Category Header - full width (overrides .survey-box 50% on lg) */}
        <div className={`survey-box element-${category.palette} p-6 mb-6`} style={{ marginBottom: "24px", width: "100%", maxWidth: "100%" }}>
          <div style={{ wordBreak: 'break-word' }}>
            <h4 className="subtitle mb-1" style={{ fontSize: PDF_FONT.sm }}>
              {t("surveyResults.category.title")}
            </h4>
            <h3 className="title-2xl mb-2" style={{ fontSize: PDF_FONT["2xl"] }}>{category.title}</h3>
            <p className="title-sm mb-2" style={{ fontSize: PDF_FONT.base }}>{category.text}</p>
            
            <table style={{ width: '100%', marginTop: '16px', fontSize: PDF_FONT.sm }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'middle', width: '50%' }}>
                    <div className="mb-2">
                      <span style={{ fontSize: PDF_FONT.base }}>
                        {rating} <span className={`text-${category.palette}`}>/</span> 6
                      </span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                    <span className="subtitle" style={{ fontSize: PDF_FONT.sm }}>
                      {t("surveyResults.category.subcategories_included")}: <span style={{ fontWeight: 700 }}>{category.skills.length}</span>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} style={{ paddingTop: '8px' }}>
                    <div className="progress-bar bg-white" style={{ width: '100%' }}>
                      <span
                        style={{
                          width: `calc(${category.sum_rating}*100%/${category.sum_responses})`,
                        }}
                        className={`bg-${category.palette}`}
                      ></span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Skills Grid - 2 columns, compact */}
        <div className="grid grid-cols-2 gap-4">
          {category.skills &&
            category.skills.map((skill, skill_ind) => {
              // Calculate skill rating from questions array
              const answer = skill?.questions?.[0]?.answer;
              const skill_rating = answer?.rating || 0;
              const possible_score = answer?.possible_score_range || 6;
              const skill_score = skill_rating % 1 === 0 ? skill_rating : (skill_rating || 0).toFixed(1);

              return (
                <div 
                  key={skill.id}
                  className="bg-[#F8F8F8] p-4 rounded-lg"
                  style={{ breakInside: "avoid" }}
                >
                  <div className="mb-3">
                    <p className="text-gray-500 mb-1" style={{ fontSize: PDF_FONT.xs }}>Skill {String(skill_ind + 1).padStart(2, '0')}</p>
                    <h4 className="title-base mb-2" style={{ fontSize: PDF_FONT.lg }}>{skill.title}</h4>
                    
                    <div className="mb-2">
                        <span style={{ fontSize: PDF_FONT.sm, verticalAlign: 'middle', display: 'inline-block' }}>
                              <span>{skill_score}</span> <span className={`text-${category.palette}`}>/</span> <span>{possible_score}</span>
                        </span>
                    </div>
                    
                    <div className="progress-bar bg-white">
                      <span
                        style={{
                          width: `calc(${skill_rating}*100%/${possible_score})`,
                        }}
                        className={`bg-${category.palette}`}
                      ></span>
                    </div>
                  </div>
                  
                  {skill?.questions?.[0]?.text && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="italic text-gray-600" style={{ fontSize: PDF_FONT.xs }}>
                        <span className="font-semibold">Question:</span> {skill.questions[0].text}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
};

// Individual Feedback Page (Admin Style) — one question chunk per PDF page
const IndividualFeedbackPage = ({ question, answers, isContinuation, t }) => {
    return (
      <div className="w-full bg-white p-6" style={{ width: "100%", backgroundColor: "#ffffff" }}>
        <h2 className="title-primary mb-8 px-4 pt-4" style={{ fontSize: PDF_FONT["3xl"] }}>{t("analyze.company.feedback_title")}</h2>
        <div className="px-4">
            <h4
                className="title-primary-base mb-4 pb-2 border-b-2 border-[#686285] text-[#686285]"
                style={{ fontSize: PDF_FONT.lg }}
            >
                {question}{isContinuation ? " …" : ""}
            </h4>
            {answers.length > 0 ? (
                <div className="space-y-3">
                    {answers.map((response, index) => (
                        <div
                            key={index}
                            className="italic text-[#686285] pl-4 border-l-2 border-[#D8D5E8]"
                            style={{ fontSize: PDF_FONT.sm }}
                        >
                            "{response}"
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-[#999] italic" style={{ fontSize: PDF_FONT.sm }}>{t("analyze.managers.no_results")}</div>
            )}
        </div>
      </div>
    );
};

// AI Summary Page (Admin Style)
const AISummaryPage = ({ questions, t }) => {
    return (
      <div className="w-full bg-white p-6" style={{ width: "100%", backgroundColor: "#ffffff" }}>
          <h2 className="title-primary mb-8 px-4 pt-4" style={{ fontSize: PDF_FONT["3xl"] }}>{t("analyze.managers.summary")}</h2>
          <div className="px-4">
             {questions && questions.length > 0 ? (
                questions.map((question, index) => (
                    <div key={question.questionId || index} className="mb-8" style={{ fontSize: PDF_FONT.base }}>
                        <h4
                            className="title-primary-base mb-4 pb-2 border-b-2 border-[#686285] text-[#686285]"
                            style={{ fontSize: PDF_FONT.lg }}
                        >
                            {question.questionText}
                        </h4>
                        <div className="pl-4 border-l-2 border-[#D8D5E8] text-[#686285] space-y-3">
                            <div>
                                <strong style={{ fontSize: PDF_FONT.base }}>
                                    {t("analyze.managers.summary_header")}
                                </strong>
                                <div className="whitespace-pre-wrap leading-relaxed mt-1" style={{ fontSize: PDF_FONT.base }}>
                                    {question.summary}
                                </div>
                            </div>
                            <p className="italic" style={{ fontSize: PDF_FONT.sm }}>
                                {t("analyze.managers.count_statement", { count: question.count })}
                            </p>
                            <div>
                                <strong style={{ fontSize: PDF_FONT.base }}>
                                    {t("analyze.managers.recommendation_header")}
                                </strong>
                                <div className="whitespace-pre-wrap leading-relaxed mt-1" style={{ fontSize: PDF_FONT.base }}>
                                    {question.recommendation}
                                </div>
                            </div>
                        </div>
                    </div>
                ))
             ) : (
                 <div className="text-[#999] italic" style={{ fontSize: PDF_FONT.sm }}>
                    {t("analyze.managers.no_results")}
                 </div>
             )}
          </div>
      </div>
    );
};


const ManagerPDFWindowContent = ({
  t,
  user,
  radarData,
  strength,
  weakness,
  categories,
  filteredAndCategorizedFeedback,
  managerSummary,
  onClose,
  pdfOptions,
}) => {
  const dashboardRef = useRef(null);
  const categoryPagesRefs = useRef([]);
  const feedbackPagesRefs = useRef([]);
  const aiSummaryRefs = useRef([]);

  // Chunk AI Summary questions (max 2 per page)
  const aiSummaryQuestionChunks = useMemo(() => {
    if (!managerSummary?.questions || managerSummary.questions.length === 0) return [];

    const chunks = [];
    for (let i = 0; i < managerSummary.questions.length; i += 2) {
      chunks.push(managerSummary.questions.slice(i, i + 2));
    }
    return chunks;
  }, [managerSummary]);

  // Chunk qualitative feedback into pages so content never clips. Each
  // page holds one question plus as many of its answers as safely fit
  // within the PDF page height (html2canvas rasterizes each ref to a
  // single page, so CSS page-breaks are not an option here).
  const feedbackPages = useMemo(() => {
    const PAGE_CHAR_BUDGET = 3200;
    const MAX_ANSWERS_PER_PAGE = 25;
    const ANSWER_OVERHEAD = 30;

    const pages = [];
    filteredAndCategorizedFeedback.forEach(([question, answers]) => {
      if (!answers || answers.length === 0) {
        pages.push({ question, answers: [], isContinuation: false });
        return;
      }

      let currentAnswers = [];
      let currentChars = 0;
      let isFirstChunk = true;

      answers.forEach((answer) => {
        const size = (answer?.length || 0) + ANSWER_OVERHEAD;
        const wouldOverflow =
          currentChars + size > PAGE_CHAR_BUDGET ||
          currentAnswers.length >= MAX_ANSWERS_PER_PAGE;
        if (wouldOverflow && currentAnswers.length > 0) {
          pages.push({
            question,
            answers: currentAnswers,
            isContinuation: !isFirstChunk,
          });
          isFirstChunk = false;
          currentAnswers = [];
          currentChars = 0;
        }
        currentAnswers.push(answer);
        currentChars += size;
      });

      if (currentAnswers.length > 0) {
        pages.push({
          question,
          answers: currentAnswers,
          isContinuation: !isFirstChunk,
        });
      }
    });

    if (pages.length === 0) {
      pages.push({ question: null, answers: [], isContinuation: false });
    }
    return pages;
  }, [filteredAndCategorizedFeedback]);

  const convertCanvasToImage = (element) => {
    if (!element) return element;

    const canvases = element.querySelectorAll("canvas");
    canvases.forEach((canvas) => {
      const img = document.createElement("img");
      img.src = canvas.toDataURL("image/png");

      const style = window.getComputedStyle(canvas);
      const hasMxAuto = canvas.className.includes("mx-auto");

      Object.assign(img.style, {
        width: style.width,
        height: style.height,
        display: style.display,
        margin: style.margin,
        marginLeft: hasMxAuto ? "auto" : style.marginLeft,
        marginRight: hasMxAuto ? "auto" : style.marginRight,
        padding: style.padding,
        maxWidth: style.maxWidth,
        maxHeight: style.maxHeight,
      });
      img.className = canvas.className;
      canvas.parentNode?.replaceChild(img, canvas);
    });

    return element;
  };

  const generatePDF = async () => {
    if (!user) return;
    try {
      const pdf = new jsPDF({
        orientation: PDF_CONFIG.orientation,
        unit: "mm",
        format: PDF_CONFIG.format,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();

      const capturePage = async (element) => {
         if (!element) return null;
         await convertCanvasToImage(element);
         const rect = element.getBoundingClientRect();
         const elementWidth = rect.width || PDF_CONFIG.targetWidth;
         const elementHeight = rect.height || PDF_CONFIG.targetHeight;
         
         return html2canvas(element, {
            scale: PDF_CONFIG.html2canvasScale,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: elementWidth,
            height: elementHeight,
            windowWidth: 1600,
            onclone: (clonedDoc) => {
                const boxes = clonedDoc.querySelectorAll(".legend-box");
                boxes.forEach(box => {
                    box.style.top = "-6px";
                });
                
                // Minimal text spacing fix - 0.2px is subtle but effective for "cramped" text
                clonedDoc.body.style.letterSpacing = '0.5px';
            }
         });
      };

      // Capture all pages in parallel preserving order
      const allElements = [
        dashboardRef.current,
        ...categoryPagesRefs.current,
        ...feedbackPagesRefs.current,
        ...aiSummaryRefs.current
      ].filter(Boolean);

      const allCanvases = await Promise.all(allElements.map(el => capturePage(el)));

      // Add to PDF
      const addCanvasToPDF = (canvas, isFirst) => {
        if (!canvas) return;
        if (!isFirst) pdf.addPage();
        
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        
        // Calculate scale to fit page width
        const scale = pageWidth / canvasWidth;
        const finalWidth = canvasWidth * scale;
        const finalHeight = canvasHeight * scale;
        
        const imgData = canvas.toDataURL("image/jpeg", PDF_CONFIG.jpegQuality);
        pdf.addImage(imgData, "JPEG", 0, 0, finalWidth, finalHeight);
      };

      allCanvases.forEach((canvas, index) => {
         addCanvasToPDF(canvas, index === 0);
      });

      pdf.save(pdfOptions.filename || "survey_results.pdf");
      onClose?.();

    } catch (error) {
      console.error("Error generating PDF", error);
      onClose?.();
    }
  };

  useEffect(() => {
    const run = async () => {
      await new Promise((r) => setTimeout(r, CHART_RENDER_DELAY_MS));
      await generatePDF();
    };
    const timeoutId = setTimeout(run, PDF_START_DELAY_MS);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white" style={{ width: "1400px" }}>
        <div ref={dashboardRef}>
            <DashboardPage
              t={t}
              user={user}
              radarData={radarData}
              strength={strength}
              weakness={weakness}
            />
        </div>

        {/* Render one page per category */}
        {categories.map((category, index) => (
             <div 
                key={category.id || index} 
                ref={el => categoryPagesRefs.current[index] = el}
             >
                <PDFCategoryPage category={category} t={t} />
             </div>
        ))}

        {feedbackPages.map((page, index) => (
            <div
                key={`feedback-page-${index}`}
                ref={el => feedbackPagesRefs.current[index] = el}
            >
                <IndividualFeedbackPage
                    question={page.question}
                    answers={page.answers}
                    isContinuation={page.isContinuation}
                    t={t}
                />
            </div>
        ))}

         {aiSummaryQuestionChunks.map((chunk, index) => (
            <div 
                key={`ai-summary-${index}`}
                ref={el => aiSummaryRefs.current[index] = el}
            >
                <AISummaryPage questions={chunk} t={t} />
            </div>
         ))}
    </div>
  );
};

export default ManagerPDFWindowContent;
